import axios from 'axios'
import Cookies from 'js-cookie'

export default {
  namespaced: true,
  state: {
    championships: [],
    temporaryChampionship: null,
    error: null,
    retryCount: 0,
    pvpOnly: false, // Add this flag to track filter state
    shouldReload: false // Novo estado para controlar recarregamento
  },
  mutations: {
    SET_CHAMPIONSHIPS(state, championships) {
      state.championships = championships
    },
    SET_TEMPORARY_CHAMPIONSHIP(state, championship) {
      state.temporaryChampionship = championship
    },
    SET_ERROR(state, error) {
      state.error = error
    },
    INCREMENT_RETRY_COUNT(state) {
      state.retryCount++;
    },
    RESET_RETRY_COUNT(state) {
      state.retryCount = 0;
    },
    RESET_FILTER_STATE(state) {
      state.pvpOnly = false;
      state.championships = []; // Clear existing championships
      state.error = null;
      state.shouldReload = true; // Forçar recarregamento ao reiniciar o estado
    },
    SET_PVP_FILTER(state, isPvpOnly) {
      state.pvpOnly = isPvpOnly;
    },
    SET_SHOULD_RELOAD(state, value) {
      state.shouldReload = value;
    }
  },
  actions: {
    async fetchChampionships({ commit, state, dispatch, rootState }, options = {}) {
      try {
        // If pvpOnly is provided in options, update the filter state
        if (options.pvpOnly !== undefined) {
          commit('SET_PVP_FILTER', options.pvpOnly);
        }
        
        // Get player ID from the playerData state
        const playerId = rootState.player.playerData?.id;
        if (!playerId) {
          console.warn('Player ID not found, fetching player data first');
          await rootState.player.fetchPlayer;
        }

        // Make request with playerId as a query parameter
        const response = await axios.get(
          `${process.env.VUE_APP_API_URL}/api/championships`,
          {
            params: {
              playerId: rootState.player.playerData?.id,
              strict: true
            },
            headers: { 
              Authorization: `Bearer ${Cookies.get('jwt')}` 
            }
          }
        ).catch(err => {
          // Handle both 400 and 500 errors
          if (err.response) {
            console.warn(`API returned ${err.response.status} error:`, err.response.data);
            
            // On server error with few retries, try again
            if (err.response.status === 500 && state.retryCount < 2) {
              commit('INCREMENT_RETRY_COUNT');
              console.log(`Retrying request (${state.retryCount}/2)...`);
              // Wait 1 second before retrying
              return new Promise(resolve => {
                setTimeout(() => {
                  resolve(dispatch('fetchChampionships'));
                }, 1000);
              });
            }
            
            // If we've retried too many times or it's a 400 error,
            // return empty data to prevent fatal error
            return { data: { data: [] } };
          }
          throw err; // Re-throw network errors, etc.
        });
        
        // Reset retry count after successful request
        commit('RESET_RETRY_COUNT');
        
        if (response.data && response.data.data) {
          // Get current player ID for filtering
          const currentPlayerId = rootState.player.playerData?.id;
          
          console.log('Championships from API:', response.data.data.length);
          
          // Transform data to a simpler format with defaults
          const championships = response.data.data
            .map(c => {
              // Check if player is participating directly from API data
              const isPlayerParticipating = Array.isArray(c.players) && 
                c.players.some(player => player.id === currentPlayerId);
              
              // Use backend's isFull property for PvP challenges
              const isPvPFull = c.isPvP && c.matches && c.matches.length > 0 && 
                (c.matches[0].isFull || (c.matches[0].challenging_2s && c.matches[0].challenging_2s.length > 0));

              // Determine if this is a death match based on rules
              const isDeathMatch = c.rules === 'untilDeath' || 
                (c.matches && c.matches.length > 0 && c.matches[0].rules === 'untilDeath');

              // Create championship object with all defaults
              return {
                id: c.id || null,
                name: c.name || 'Torneio Sem Nome',
                arena: {
                  id: c.arena?.id || null,
                  name: c.arena?.name || 'Arena Desconhecida'
                },
                reward: c.reward || 0,
                status: c.status || 'pending',
                vacancies: c.vacancies ?? 0,
                players: c.players || [],
                matches: (c.matches || []).map(match => ({
                  id: match.id || null,
                  challenging_1s: match.challenging_1s || [],
                  challenging_2s: match.challenging_2s || [],
                  victory: match.victory || 0,
                  canStart: match.canStart ?? true,
                  rules: match.rules || 'normal',
                  rank: match.rank || 'F',
                  battle: match.battle || null,
                  createdAt: match.createdAt || new Date(),
                  isFull: match.isFull || (match.challenging_1s?.length > 0 && match.challenging_2s?.length > 0),
                  isDeathMatch: match.rules === 'untilDeath'
                })),
                isFinish: c.isFinish ?? false,
                champion: c.champion || null,
                isPlayerParticipating,
                isPvP: c.isPvP || false,
                minReward: c.minReward || 0,
                minHealthPercentage: c.minHealthPercentage || 0,
                maxOpponentRank: c.maxOpponentRank || null,
                maxOpponentAttack: c.maxOpponentAttack || null,
                untilDeath: isDeathMatch,
                rewardMultiplier: isDeathMatch ? 3 : 1,
                isFull: isPvPFull
              };
            })
            .filter(c => {
              // If pvpOnly flag is set, only show PVP championships
              if (state.pvpOnly) {
                return c.isPvP;
              }
              
              // Otherwise, filter PVP championships for non-participants
              if (c.isPvP && !c.isPlayerParticipating) {
                return false;
              }
              
              return true;
            });
          
          console.log('Championships after filtering:', championships.length);
          
          commit('SET_CHAMPIONSHIPS', championships);
          commit('SET_ERROR', null);
          return championships;
        } else {
          commit('SET_CHAMPIONSHIPS', []);
          commit('SET_ERROR', 'Nenhum campeonato encontrado');
          return [];
        }
      } catch (error) {
        console.error('Error fetching championships:', error);
        commit('SET_CHAMPIONSHIPS', []);
        
        const errorMsg = 'Erro ao carregar campeonatos. Por favor, tente novamente mais tarde.';
        commit('SET_ERROR', errorMsg);
        return [];
      }
    },
    async generateTemporaryChampionship({ commit }, { rankArray, arenaId }) {
      try {
        if (!rankArray || !rankArray.length) {
          rankArray = ['F'];
        }
        
        const response = await axios.post(
          `${process.env.VUE_APP_API_URL}/api/championships/generate`,
          {
            rankArray,
            arenaId: arenaId || 1
          },
          {
            headers: { 
              Authorization: `Bearer ${Cookies.get('jwt')}` 
            }
          }
        );
        
        let championshipData = response.data.data;
        
        if (championshipData) {
          championshipData = {
            name: championshipData.name || 'Torneio Temporário',
            arena: championshipData.arena || { id: arenaId || 1 },
            reward: championshipData.reward || 5000,
            matches: (championshipData.matches || []).map(match => ({
              ...match,
              challenging_1s: match.challenging_1s || [],
              victory: 0
            })),
            oficial: championshipData.oficial ?? true,
            vacancies: championshipData.vacancies ?? 1
          };
        }
        
        commit('SET_TEMPORARY_CHAMPIONSHIP', championshipData);
        return championshipData;
      } catch (error) {
        console.error('Error generating temporary championship:', error);
        commit('SET_ERROR', 'Erro ao gerar campeonato temporário. Por favor, tente novamente.');
        throw error;
      }
    },
    async createChampionship({ dispatch, rootState }, championshipData) {
      try {
        const playerId = rootState.player.playerData?.id;
        if (!playerId) {
          throw new Error('Player ID not found');
        }

        const dataToSend = {
          data: {
            name: championshipData.name,
            arena: championshipData.arena,
            oficial: championshipData.oficial,
            reward: championshipData.reward,
            players: [playerId],
            vacancies: championshipData.vacancies,
            matches: championshipData.matches
          }
        };

        const response = await axios.post(
          `${process.env.VUE_APP_API_URL}/api/championships`,
          dataToSend,
          {
            headers: { 
              Authorization: `Bearer ${Cookies.get('jwt')}` 
            }
          }
        );
        
        await dispatch('fetchChampionships');
        return response.data.data;
      } catch (error) {
        console.error('Error creating championship:', error);
        throw error;
      }
    },
    async findNextMatch(_, { championshipId, currentMatchId }) {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_URL}/api/championships/${championshipId}`,
          {
            headers: { Authorization: `Bearer ${Cookies.get('jwt')}` }
          }
        );
        
        const championship = response.data.data;
        if (!championship?.matches) return null;
        
        const sortedMatches = [...championship.matches].sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        );
        
        const currentIndex = sortedMatches.findIndex(m => m.id === currentMatchId);
        if (currentIndex === -1) return null;
        
        const nextMatch = sortedMatches.slice(currentIndex + 1).find(m => m.victory === 0);
        
        return nextMatch || null;
      } catch (error) {
        console.error('Failed to find next match:', error);
        throw error;
      }
    },
    async claimReward({ dispatch }, championshipId) {
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_URL}/api/championships/${championshipId}/claim-reward`,
          {},
          {
            headers: { Authorization: `Bearer ${Cookies.get('jwt')}` }
          }
        );

        await dispatch('player/loadPlayerData', null, { root: true });

        await axios.delete(
          `${process.env.VUE_APP_API_URL}/api/championships/${championshipId}`,
          {
            headers: { Authorization: `Bearer ${Cookies.get('jwt')}` }
          }
        );

        await dispatch('fetchChampionships');

        return response.data;
      } catch (error) {
        console.error('Error claiming championship reward:', error);
        throw error;
      }
    },
    async createPvPChallenge({ dispatch }, { fighterId, ...challengeData }) {
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_URL}/api/championships/pvp/challenge/${fighterId}`,
          {
            minReward: parseInt(challengeData.minReward) || 1000,
            minHealthPercentage: parseInt(challengeData.minHealthPercentage) || 50,
            maxOpponentRank: challengeData.maxOpponentRank || null,
            maxOpponentAttack: parseInt(challengeData.maxOpponentAttack) || null,
            untilDeath: challengeData.untilDeath || false
          },
          {
            headers: { 
              Authorization: `Bearer ${Cookies.get('jwt')}` 
            }
          }
        );

        await dispatch('fetchChampionships');
        
        return response.data;
      } catch (error) {
        console.error('Failed to create PvP challenge:', error);
        throw error.response?.data?.error || error;
      }
    },
    async acceptPvPChallenge({ dispatch }, { championshipId, fighterId }) {
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_URL}/api/championships/${championshipId}/accept/${fighterId}`,
          {},
          {
            headers: { 
              Authorization: `Bearer ${Cookies.get('jwt')}` 
            }
          }
        );

        await dispatch('fetchChampionships');
        
        return response.data;
      } catch (error) {
        console.error('Failed to accept PvP challenge:', error);
        
        if (error.response?.data?.error?.message?.includes('already has a challenger')) {
          throw new Error('Este desafio já foi aceito por outro lutador.');
        }
        
        throw error.response?.data?.error || error;
      }
    },
    async fetchPvPChallenges({ commit, rootState }) {
      try {
        commit('SET_PVP_FILTER', true); // Set PVP filter flag
        
        const currentPlayerId = rootState.player.playerData?.id;
        console.log('Current player ID from state:', currentPlayerId);
        
        const playerFighters = rootState.player.playerData?.fighters || [];
        const playerFighterIds = playerFighters.map(f => f.id);
        
        const response = await axios.get(
          `${process.env.VUE_APP_API_URL}/api/championships/pvp`,
          {
            headers: { 
              Authorization: `Bearer ${Cookies.get('jwt')}` 
            }
          }
        );
        
        if (response.data && response.data.data) {
          console.log('PvP challenges from API:', response.data);
          const challenges = response.data.data.map(challenge => {
            let isFull = false;
            
            if (challenge.matches && challenge.matches.length > 0) {
              challenge.matches = challenge.matches.map(match => {
                const matchIsFull = match.isFull || 
                                  (match.challenging_2s && match.challenging_2s.length > 0);
                
                if (matchIsFull) {
                  isFull = true;
                }
                
                if (match.challenging_1s && match.challenging_1s.length > 0) {
                  match.challenging_1s = match.challenging_1s.map(fighter => {
                    const isCurrentPlayerFighter = playerFighterIds.includes(fighter.id);
                    if (isCurrentPlayerFighter) {
                      if (!fighter.player) fighter.player = {};
                      fighter.player.id = currentPlayerId;
                      fighter.ownedByCurrentPlayer = true;
                    }
                    return fighter;
                  });
                }
                
                if (match.challenging_2s && match.challenging_2s.length > 0) {
                  match.challenging_2s = match.challenging_2s.map(fighter => {
                    const isCurrentPlayerFighter = playerFighterIds.includes(fighter.id);
                    if (isCurrentPlayerFighter) {
                      if (!fighter.player) fighter.player = {};
                      fighter.player.id = currentPlayerId;
                      fighter.ownedByCurrentPlayer = true;
                    }
                    return fighter;
                  });
                }
                
                const isDeathMatch = match.rules === 'untilDeath';
                
                return {
                  ...match,
                  isFull: matchIsFull,
                  isDeathMatch
                };
              });
            }
            
            const creatorFighter = challenge.matches?.[0]?.challenging_1s?.[0];
            const isPlayerChallenge = creatorFighter?.ownedByCurrentPlayer || 
                                     playerFighterIds.includes(creatorFighter?.id);
            
            const isDeathMatch = challenge.rules === 'untilDeath' || 
              (challenge.matches && challenge.matches.length > 0 && 
               challenge.matches[0]?.rules === 'untilDeath');
            
            const baseReward = challenge.minReward || 0;
            const actualReward = isDeathMatch ? baseReward * 3 : baseReward;
            
            return {
              ...challenge,
              minReward: baseReward,
              reward: actualReward,
              isPlayerChallenge,
              isFull,
              untilDeath: isDeathMatch,
              rewardMultiplier: isDeathMatch ? 3 : 1,
              displayReward: {
                base: baseReward,
                final: actualReward,
                multiplier: isDeathMatch ? 3 : 1
              }
            };
          });
          
          commit('SET_CHAMPIONSHIPS', challenges);
          commit('SET_ERROR', null);
          return challenges;
        } else {
          commit('SET_CHAMPIONSHIPS', []);
          commit('SET_ERROR', 'Nenhum desafio PvP encontrado');
          return [];
        }
      } catch (error) {
        console.error('Error fetching PvP challenges:', error);
        commit('SET_CHAMPIONSHIPS', []);
        commit('SET_ERROR', 'Erro ao carregar desafios PvP');
        return [];
      }
    },
    async deleteChampionship({ dispatch }, championshipId) {
      try {
        await axios.delete(
          `${process.env.VUE_APP_API_URL}/api/championships/${championshipId}`,
          {
            headers: { Authorization: `Bearer ${Cookies.get('jwt')}` }
          }
        );
        await dispatch('fetchChampionships');
      } catch (error) {
        console.error('Error deleting championship:', error);
        throw error;
      }
    },
  }
}
